import React from 'react'
import * as config from 'config'
import Button from 'uiComponents/buttons'
import { Modal, ModalBody, ModalFooter } from 'uiComponents/popups/modal.new'
import { LucideIcon } from 'utils/lucideIcon'
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard'
import './connectApp.scss'
import { Trans, useTranslation } from 'translations'

interface ConnectAppModalProps {
    onDismiss: () => void
}

const CodeBlock: React.FC<{ code: string }> = ({ code }) => {
    const { copied, copy } = useCopyToClipboard()
    const { t } = useTranslation()

    return (
        <div className="connect-app-code">
            <pre>{code}</pre>
            <button type="button" className="connect-app-copy" onClick={() => copy(code)}>
                <LucideIcon iconName={copied ? 'Check' : 'Copy'} size={14} />
                {copied
                    ? t('settings.personalSettings.serviceApps.connectApp.copied')
                    : t('settings.personalSettings.serviceApps.connectApp.copy')}
            </button>
        </div>
    )
}

const trimSlash = (url: string) => url.replace(/\/+$/, '')

export const ConnectAppModal: React.FC<ConnectAppModalProps> = ({ onDismiss }) => {
    const { t } = useTranslation()
    const mcpEndpoint = config.get('mcp-endpoint')
    const identityEndpoint = config.get('identity-endpoint')

    const mcpUrl = mcpEndpoint ? `${trimSlash(mcpEndpoint)}/mcp` : null
    const tokenUrl = identityEndpoint ? `${trimSlash(identityEndpoint)}/oauth/token/` : null

    const directCall =
        mcpUrl &&
        [
            'curl -u "$CLIENT_ID:$CLIENT_SECRET" \\',
            "  -H 'Content-Type: application/json' \\",
            "  -H 'Accept: application/json, text/event-stream' \\",
            '  -d \'{"jsonrpc":"2.0","id":1,"method":"tools/list"}\' \\',
            `  ${mcpUrl}`,
        ].join('\n')

    const mintToken =
        tokenUrl &&
        [
            `JWT=$(curl -s -X POST ${tokenUrl} \\`,
            '  -d "grant_type=client_credentials" \\',
            '  -d "client_id=$CLIENT_ID" \\',
            '  -d "client_secret=$CLIENT_SECRET" \\',
            '  | jq -r .access_token)',
        ].join('\n')

    const bearerCall =
        mcpUrl &&
        [
            'curl -H "Authorization: Bearer $JWT" \\',
            "  -H 'Content-Type: application/json' \\",
            "  -H 'Accept: application/json, text/event-stream' \\",
            '  -d \'{"jsonrpc":"2.0","id":1,"method":"tools/list"}\' \\',
            `  ${mcpUrl}`,
        ].join('\n')

    return (
        <Modal
            title={t('settings.personalSettings.serviceApps.connectApp.title')}
            onDismiss={onDismiss}
            className="connect-app-modal"
        >
            <ModalBody>
                <p className="connect-app-intro">{t('settings.personalSettings.serviceApps.connectApp.intro')}</p>

                {mcpUrl && directCall && (
                    <section className="connect-app-step">
                        <h5>
                            <span className="connect-app-step-number">1</span>
                            {t('settings.personalSettings.serviceApps.connectApp.step1Title')}
                        </h5>
                        <CodeBlock code={directCall} />
                        <p>
                            <Trans
                                i18nKey="settings.personalSettings.serviceApps.connectApp.step1Body"
                                values={{ mcpUrl }}
                                components={{ code: <code /> }}
                            />
                        </p>
                    </section>
                )}

                {tokenUrl && mintToken && (
                    <section className="connect-app-step">
                        <h5>
                            <span className="connect-app-step-number">2</span>
                            {t('settings.personalSettings.serviceApps.connectApp.step2Title')}
                        </h5>
                        <CodeBlock code={mintToken} />
                        <p>
                            <Trans
                                i18nKey="settings.personalSettings.serviceApps.connectApp.step2Body"
                                components={{ code: <code /> }}
                            />
                        </p>
                        {bearerCall && <CodeBlock code={bearerCall} />}
                    </section>
                )}

                <p className="connect-app-footnote">{t('settings.personalSettings.serviceApps.connectApp.footnote')}</p>
            </ModalBody>
            <ModalFooter>
                <Button kind="action" onClick={onDismiss}>
                    {t('settings.personalSettings.serviceApps.connectApp.done')}
                </Button>
            </ModalFooter>
        </Modal>
    )
}
